import React, { useCallback, useEffect, useState } from 'react';
import { AfterPayBanner, AlertsList, EmptyProductsFallback } from '../../components';
import {
	BREADCRUMB,
	HELPBUTTON,
	PAGETITLE,
	PAYPALBANNER,
	PRODUCTTILES,
	PROMOMSG
} from '../../constants/rootElements';
import { useFeeAlertInjectionScript, useGoogleAnalyticsHandler } from "../../hooks";
import { appendTermsAndConditionsLink } from '../../util/appendTermsAndConditionsLink';
import { gaPageLoad } from "../../util/googleAnalytics";
import { isAllSubCatalogProductsEmpty } from '../../util/isAllSubCatalogProductsEmpty';
import { Breadcrumb } from '../breadcrumb';
import { HelpButton } from '../help';
import { TitleBar } from '../productBrowseTitle';
import { AffiliationStoleProductTile } from './affiliationStoleProductTile';

export const AffiliationStolePage = React.memo(({ appData }) => {
	const [pbData, setPbData] = useState({});
	const [catalogData, setCatalogData] = useState({});
	const [breadcrumbData, setBreadcrupmData] = useState([]);
	const [promoMessages, setPromoMessages] = useState([]);
	const [feeAlertRequestData, setFeeAlertRequestData] = useState(null);
	const [googleAnalyticsRequestData, setGoogleAnalyticsRequestData] = useState(null);
    const [currencyCode, setCurrencyCode] = useState(null);

	useFeeAlertInjectionScript(feeAlertRequestData)
	const { selectItemTrackingGASY25, viewModalTrackingGASY25, addToCartTrackingGASY25 } = useGoogleAnalyticsHandler(googleAnalyticsRequestData);

	const AffiliationStoleBreadcrumb = Boolean(
		appData?.pagecontentData?.find((data) => data?.contentType === 'Navigation')
	);
	const AffiliationStoleTitleBar = appData?.pagecontentData?.find(
		(data) => data?.contentType === 'ProductBrowseTitle'
	);
	const ProductBrowseProducts = appData?.pagecontentData?.find(
		(data) => data?.contentType === 'ProductBrowseProducts'
	);
	const PaypalBannerData = appData?.pagecontentData?.find(
		(data) => data?.contentType === 'PaypalId'
	);
	const AffiliationStoleFilterTile = appData?.pagecontentData?.find(
		(data) => data?.contentType === 'ProductBrowseFiltersBar'
	);
	const ProductBrowsePromoMsg = Boolean(
		appData?.pagecontentData?.find(
			(data) => data.contentType === 'ProductBrowsePromoMsg'
		)
	);

	const getBreadcrumb = useCallback(() => {
		BREADCRUMB.render(<Breadcrumb pbData={breadcrumbData} />);
	}, [breadcrumbData]);

	const getHelpButton = useCallback(() => {
        HELPBUTTON.render(<HelpButton />);
  },[]);

	const getTitleBar = useCallback(() => {
		PAGETITLE.render(
			<TitleBar
				pageContent={AffiliationStoleTitleBar}
				affiliationData={appData?.affiliationData}
			/>
		);
	}, []);

	const getPromoMsg = useCallback(() => {		
		const messagesWithLink = appendTermsAndConditionsLink(promoMessages);
		PROMOMSG.render(<AlertsList messages={messagesWithLink} />);
	}, [promoMessages]);

	const productTiles = useCallback(() => {
		const isSubCatalogProductsEmpty = isAllSubCatalogProductsEmpty(catalogData)
		const isProductsEmpty = !Boolean(pbData?.products?.length)

		PRODUCTTILES.render(
			isProductsEmpty && isSubCatalogProductsEmpty
				? <EmptyProductsFallback
					productBrowseData={pbData}
				/>
				: <AffiliationStoleProductTile
					pbData={pbData}
					pageContent={ProductBrowseProducts}
					filterData={appData?.filterData}
					serviceData={catalogData}
					fieldToCompare={AffiliationStoleFilterTile?.filedtocompare}
					googleAnalytics={{ 
						selectItemTrackingGASY25, 
						viewModalTrackingGASY25, 
						addToCartTrackingGASY25,
						catalogOSR: googleAnalyticsRequestData?.catalogOsr
					}}
				/>
		)
		// eslint-disable-next-line
	}, [catalogData, selectItemTrackingGASY25]);

	const paypalBanner = useCallback(() => {
		PAYPALBANNER.render(<AfterPayBanner currencyCode={currencyCode} />);
		},[PaypalBannerData, currencyCode])

	useEffect(() => {
		Object.keys(appData?.productBrowseData).map((key) => {
			if (key !== 'metaData') {
				const productGroups = appData?.productBrowseData[key]?.productGroups;
				productGroups && Object.keys(productGroups).map(
						(catObj) => {
							const customerNumber = appData.productBrowseData.metaData.customerNumber;
							const catalog = appData.productBrowseData[key];
							setFeeAlertRequestData({
								containerSelector: '#promoMsg',
								customerId: customerNumber,
								catalogOsr: catalog.catalogOsr,
							});
							setGoogleAnalyticsRequestData({
								customerNumber,
								publishedCatalogId: catalog.publishedCatalogId,
								sourceCatalogType: catalog.sourceCatalogType,
								catalogOsr: catalog.catalogOsr
							});
							const groupData = productGroups[catObj];
							setPbData(groupData);
							const _currencyCode = groupData?.products?.[0]?.currencyCode;
							_currencyCode && setCurrencyCode(_currencyCode);
							setCatalogData(appData?.productBrowseData[key]);
							setPromoMessages(appData.productBrowseData[key].promoMessagesV2);
							setBreadcrupmData(groupData);
							return true;
						}
					);
			}
			return true;
		});
	}, [appData?.productBrowseData]);

	useEffect(()=>{
		if(appData.productBrowseData && Object.keys(appData.productBrowseData).length>0){
			gaPageLoad();
		}
	},[])
	return (
		<>
			{AffiliationStoleTitleBar &&
				Object.keys(AffiliationStoleTitleBar)?.length > 0 &&
				getTitleBar()
			}
			{ProductBrowsePromoMsg && promoMessages?.length > 0 && getPromoMsg()}
			{AffiliationStoleBreadcrumb && getBreadcrumb()}
			{ProductBrowseProducts &&
				Object.keys(ProductBrowseProducts)?.length > 0 &&
				productTiles()
			}
			{
        getHelpButton()
      }
			{PaypalBannerData &&
				Object.keys(PaypalBannerData)?.length > 0 &&
				paypalBanner()
			}
		</>
	);
});
